// Monitoring (M67, docs/monitoring.md): what the apps report about their
// own errors (D254), how it is scrubbed before it leaves the device (and
// again on the server), and the thresholds the server watches (D255).
import { z } from 'zod';
export const CLIENT_ERROR_PLATFORMS = ['web', 'android', 'ios'];
/** Failures the apps count where the code already knows the outcome. */
export const CLIENT_FAILURE_COUNTERS = [
    'send_failed',
    'upload_failed',
    'call_setup_failed',
    // A wrapped chat or family key refused: its wrapper isn't a recorded
    // device of anyone in that chat or family (M73, D301).
    'e2ee_wrapper_refused',
    // The device record couldn't be opened, so a record for that visit only
    // was used (M73, D300).
    'e2ee_record_unavailable',
    // A wrapped key refused although its wrapper was someone's recorded key
    // in that chat or family, for an epoch this device hadn't opened before
    // the key went or they left: usually an honest lockout (M73, D301).
    'e2ee_wrapper_lapsed',
    // Envelope v2 (M76, D327): a sealed message or record didn't open with
    // the key held (sender, type, epoch, chat, revision or record id don't
    // match what it was sealed for).
    'e2ee_envelope_unverified',
    // An edit older than one this device already showed (D326).
    'e2ee_envelope_stale_edit',
    // A v1 envelope under an epoch above this device's v1 ceiling (D328).
    'e2ee_envelope_downgrade',
    // The server named a chat's or family's current key epoch below one this
    // device had opened or made there, twice: nothing was sealed under it
    // (D341).
    'e2ee_epoch_rollback',
    // A message or family record with no encryption, dated after its chat's
    // or family's encryption began as this device knows it: not shown (D347).
    'e2ee_plaintext_downgrade',
    // Post-quantum key wrapping (M78, docs/post-quantum.md §8). A v1 copy
    // from a wrapper with an ML-KEM key, above this device's PQ ceiling
    // (D339).
    'e2ee_pq_downgrade',
    // A v2 copy didn't open with this device's keys (§4.3).
    'e2ee_pq_unwrap_failed',
    // A keys answer left out the ML-KEM key recorded for a device, or gave
    // one failing FIPS 203's check (D338).
    'e2ee_pq_key_missing',
    // A keys answer named another ML-KEM key for a recorded one (D338).
    'e2ee_pq_key_changed',
    // The server lists another ML-KEM key for this device than its own
    // (§4.3's self-check).
    'e2ee_pq_own_key_mismatch',
    // This device's ML-KEM key couldn't be made, kept or published (§6.5).
    'e2ee_pq_key_unavailable',
];
export const CLIENT_ERROR_LIMITS = {
    MESSAGE: 500,
    STACK: 4096,
    ROUTE: 100,
    APP_VERSION: 40,
    ITEMS: 20,
    /** Error items a device sends per minute at most (counters don't count). */
    ERRORS_PER_MINUTE: 5,
    /** How often a device sends a batch at most, in ms. */
    BATCH_EVERY_MS: 10_000,
    /** A batch stays under the server's 32 KB body limit. */
    BATCH_BYTES: 30_000,
};
// Control characters other than newline and tab have no place in an error
// text, and Postgres refuses NUL outright: refused here so a report can
// never make the server fail (D254).
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/;
const printable = (max) => z
    .string()
    .max(max)
    .refine((value) => !CONTROL.test(value), 'control characters');
export const clientErrorItemSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('error'),
        message: printable(CLIENT_ERROR_LIMITS.MESSAGE).pipe(z.string().trim().min(1)),
        stack: printable(CLIENT_ERROR_LIMITS.STACK).optional(),
        route: printable(CLIENT_ERROR_LIMITS.ROUTE)
            .pipe(z.string().trim())
            .optional(),
    }),
    z.object({
        kind: z.literal('counter'),
        name: z.enum(CLIENT_FAILURE_COUNTERS),
        count: z.number().int().min(1).max(1000),
    }),
]);
const appVersionSchema = printable(CLIENT_ERROR_LIMITS.APP_VERSION).pipe(z.string().trim().min(1));
/** The report without its items checked: the server validates each on its own. */
export const clientErrorEnvelopeSchema = z.object({
    platform: z.enum(CLIENT_ERROR_PLATFORMS),
    appVersion: appVersionSchema,
    items: z.array(z.unknown()).min(1).max(CLIENT_ERROR_LIMITS.ITEMS),
});
export const clientErrorReportSchema = z.object({
    platform: z.enum(CLIENT_ERROR_PLATFORMS),
    appVersion: appVersionSchema,
    items: z.array(clientErrorItemSchema).min(1).max(CLIENT_ERROR_LIMITS.ITEMS),
});
// ---- Scrubbing (D254, §3) ----
const JWT = /\b[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g;
const BEARER = /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi;
const URL_RE = /\b(?:https?|wss?):\/\/[^\s'"<>)\]]+/gi;
const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const UUID = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
// An @handle (user or family, D215 allows hyphens) after any non-word
// boundary: start, whitespace, brackets, quotes, colons. `@fastify/jwt`
// inside a path is preceded by `/`, so it stays.
const HANDLE = /(^|[^\w@./-])@[a-z0-9_-]{3,40}\b/g;
// Opaque secrets: a long run of hex or base64 characters.
const OPAQUE = /\b[A-Za-z0-9_-]{32,}\b/g;
const OPAQUE_SEGMENT = /^[A-Za-z0-9_-]{16,}$/;
function scrubUrl(url) {
    const match = /^((?:https?|wss?):\/\/[^/?#]+)([^?#]*)(\?[^#]*)?(#.*)?$/i.exec(url);
    if (!match)
        return '[url]';
    const [, origin, path = '', query] = match;
    const segments = path
        .split('/')
        .map((segment) => (OPAQUE_SEGMENT.test(segment) ? '[token]' : segment));
    return `${origin}${segments.join('/')}${query ? '?[query]' : ''}`;
}
/**
 * Removes what could identify a person, a device or a session from an
 * error message or stack (D254): tokens, URLs with secrets or queries,
 * emails, ids and handles. Applied on the device before sending and again
 * on the server; a stack's file paths and line numbers survive it.
 */
export function scrubErrorText(text) {
    return text
        .replace(JWT, '[jwt]')
        .replace(BEARER, 'Bearer [token]')
        .replace(URL_RE, (url) => scrubUrl(url))
        .replace(EMAIL, '[email]')
        .replace(UUID, '[id]')
        .replace(HANDLE, '$1@[handle]')
        .replace(OPAQUE, '[token]');
}
// ---- Thresholds (D255) ----
export const ALERT_THRESHOLDS = [
    'server_errors',
    'canary',
    'push_failures',
    'scheduler_lag',
    'sweep_overdue',
    'new_error_group',
    'storage',
];
export const ALERT_THRESHOLD_LABELS = {
    server_errors: 'Server errors above 1%',
    canary: 'Canary failed twice',
    push_failures: 'Push failures above 10%',
    scheduler_lag: 'Scheduled messages late',
    sweep_overdue: 'A sweep is overdue',
    new_error_group: 'New error since this build',
    storage: 'Storage check failed',
};
/** Thresholds that mean people are affected: they turn /health/ready 503 (D252). */
export const USER_FACING_THRESHOLDS = [
    'server_errors',
    'canary',
    'storage',
    'scheduler_lag',
];
export const HEALTH_WINDOWS = ['24h', '7d'];
/** The fix to suggest for a failure reason, when it is a known one. */
export function pushFailureHint(transport, reason) {
    if (reason.startsWith('auth_failed')) {
        return transport === 'fcm'
            ? 'Check FCM_CLIENT_EMAIL and FCM_PRIVATE_KEY'
            : 'Check APNS_KEY_ID, APNS_TEAM_ID and APNS_KEY_P8';
    }
    if (transport === 'fcm') {
        if (reason === 'SENDER_ID_MISMATCH' || reason === 'PERMISSION_DENIED') {
            return "FCM_PROJECT_ID and the service account must be the app's Firebase project";
        }
        if (reason === 'THIRD_PARTY_AUTH_ERROR') {
            return 'THIRD_PARTY_AUTH_ERROR is about APNs credentials in Firebase, not Android';
        }
    }
    if (transport === 'apns') {
        if (reason === 'InvalidProviderToken' ||
            reason === 'ExpiredProviderToken' ||
            reason === 'MissingProviderToken') {
            return 'Check APNS_KEY_ID, APNS_TEAM_ID and APNS_KEY_P8';
        }
        if (reason === 'TopicDisallowed' || reason === 'BadTopic') {
            return 'Check APNS_BUNDLE_ID';
        }
    }
    return null;
}
//# sourceMappingURL=monitoring.js.map